import { useSelector } from 'react-redux'
import { FieldsIcon } from '@gocanvas/gcds/icons'
import { SettingsLabel } from 'apps/studio/components/shared/inputs/SettingsLabel'
import { ConvertToPrefilledTable } from 'apps/studio/components/modals/ConvertToPrefilledTable'
import { BackgroundColor as defaultBackgroundColorConfig } from 'apps/studio/components/settings/fields/Base/design'
import {
  activeTableSheetEntriesSelector,
  activeTableSheetKeyFieldSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { eligibleKeyFieldEntries } from 'apps/builder/store/selectors'
import { ensureUniqueDescription } from 'apps/builder/lib/utils'
import { ModernOptionLabelWithTooltip } from 'apps/builder/components/shared/inputs/modern'
import { lib } from '@gocanvas/client'
const { LABELS_ABOVE_STYLE, TABLE_STYLE, TABLE_PRE_POPULATED_STYLE } = lib

export const BackgroundColor = {
  ...defaultBackgroundColorConfig,
  // Table-only — pageStyles would paint every field on the page
  updateType: 'layoutStyles',
}

export const TableName = {
  label: 'Title',
  settingType: 'longTextInput',
  updateType: 'sheet',
  property: 'description',
  additionalClasses: 'textarea--constrained',
  validationFunction: (sheetId, updatedValue, state) => {
    const sheets = sheetsSelector(state) || []
    return ensureUniqueDescription(sheets, updatedValue, sheetId)
  },
}

export const ShowScreenName = {
  label: 'Hide table header on PDF',
  settingType: 'toggleInput',
  updateType: 'layoutStyles',
  property: 'hide_table_title',
  choices: [
    { value: true, previousValue: false },
    { value: false, previousValue: true },
  ],
}

export const HideColumnHeader = {
  label: 'Hide column header on PDF',
  settingType: 'toggleInput',
  updateType: 'layoutStyles',
  property: 'hide_column_header',
  choices: [
    { value: true, previousValue: false },
    { value: false, previousValue: true },
  ],
}

const TableStyle = {
  label: 'Table Style',
  settingType: 'selectInput',
  updateType: 'sheet',
  property: 'style',
  additionalValueMatches: {
    [TABLE_PRE_POPULATED_STYLE]: TABLE_STYLE,
  },
  choices: [
    {
      value: TABLE_STYLE,
      label: 'Table',
    },
    { value: LABELS_ABOVE_STYLE, label: 'Labels Above' },
  ],
}

const DisplayFieldSelect = {
  label: 'Display field',
  settingType: 'selectInput',
  updateType: 'sheet',
  property: 'display_entry',
  nestedProp: 'guid',
  dynamicChoices: () => {
    const entries = useSelector(eligibleKeyFieldEntries) || []
    return entries.map(({ guid, label }) => ({ value: guid, label }))
  },
}

const TransposeToggle = {
  label: 'Transpose',
  settingType: 'toggleInput',
  updateType: 'noop',
  property: 'transpose',
  isDisabled: () => true,
  choices: [
    { value: true, previousValue: false },
    { value: false, previousValue: true },
  ],
}

const ColumnsInput = {
  label: 'Columns',
  settingType: 'tableDimensionInput',
  updateType: 'layoutMeta',
  property: 'column_count',
}

const RowsInput = {
  label: 'Rows',
  settingType: 'tableDimensionInput',
  updateType: 'layoutMeta',
  property: 'row_count',
}

const AllowDuplicateToggle = {
  label: 'Allow duplicate entries',
  settingType: 'toggleInput',
  updateType: 'sheet',
  property: 'allow_duplicate',
  syncChildSheet: true,
  choices: [
    { value: true, previousValue: false },
    { value: false, previousValue: true },
  ],
}

export const PreFill = {
  label: 'Prefilled',
  settingType: 'toggleInput',
  updateType: 'sheet',
  property: 'style',
  tooltipContent: `Turning on pre-fill automatically adds every item from the dropdown into the table as rows, so you just review and remove any that don't apply. Enabling this setting will change the key field in your table to a dropdown.`,
  choices: [
    {
      value: TABLE_PRE_POPULATED_STYLE,
      label: 'Prefill',
      previousValue: TABLE_STYLE,
    },
    {
      value: TABLE_STYLE,
      label: 'No Prefill',
      previousValue: TABLE_PRE_POPULATED_STYLE,
    },
  ],
  isDisabled: ({ style }) =>
    useSelector(activeTableSheetKeyFieldSelector)?.type !== 'Dropdown' ||
    style === LABELS_ABOVE_STYLE,
  DisabledModalAction: ConvertToPrefilledTable,
}

const asVisibilityChoices = labels => labels.map((label, value) => ({ label, value }))

const pdfVisibilityChoices = asVisibilityChoices([
  'Always',
  'Never',
  'Only if field is not empty',
  'Only if changed',
  'Only if changed & not empty',
  'Only to addresses on the Email Options Page',
])

const webVisibilityChoices = asVisibilityChoices([
  'Always',
  'Never',
  'Only if field is not empty',
  'Only if changed',
  'Only if changed & not empty',
])

const MISMATCHED_VISIBILITY_LABEL =
  'Fields on this screen have been set to a different visibility option'

const visibilityFormatOptionLabel = ({ label, isDisabled, tooltipText }) => (
  <ModernOptionLabelWithTooltip
    label={label}
    isDisabled={isDisabled}
    tooltipText={tooltipText}
  />
)

// pdf_visibility uses 6 for the "mismatched" sentinel because 0..5 are valid
// enum values; web_visibility only uses 0..4 so -1 is safe there. Keep the
// sentinel out of choices unless entries disagree, so the dropdown never
// shows an option the user can pick.
const buildVisibilitySelect = ({
  label,
  property,
  baseChoices,
  mismatchValue,
}) => ({
  label,
  settingType: 'selectInput',
  updateType: 'sheetEntries',
  property,
  labelAbove: true,
  mismatchValue,
  dynamicChoices: () => {
    const entries = useSelector(activeTableSheetEntriesSelector) || []
    if (!entries.length) return baseChoices

    const first = entries[0][property]
    const mismatched = entries.some(entry => entry[property] !== first)
    if (!mismatched) return baseChoices

    return [
      ...baseChoices,
      { value: mismatchValue, label: MISMATCHED_VISIBILITY_LABEL, isDisabled: true },
    ]
  },
  additionalProps: {
    formatOptionLabel: visibilityFormatOptionLabel,
  },
})

export const OnPdfSelect = buildVisibilitySelect({
  label: 'On PDF',
  property: 'pdf_visibility',
  baseChoices: pdfVisibilityChoices,
  mismatchValue: 6,
})

export const OnSubmissionSelect = buildVisibilitySelect({
  label: 'On Submission',
  property: 'web_visibility',
  baseChoices: webVisibilityChoices,
  mismatchValue: -1,
})

export const TableStandardSettings = [
  TableName,
  ShowScreenName,
  HideColumnHeader,
  TableStyle,
  DisplayFieldSelect,
  TransposeToggle,
  ColumnsInput,
  RowsInput,
  AllowDuplicateToggle,
  PreFill,
  SettingsLabel('Show Fields'),
  OnPdfSelect,
  OnSubmissionSelect,
  SettingsLabel('Design'),
  BackgroundColor,
]

export const TABLE_SETTINGS_PANEL = 'Table Settings'

export const TableSettingsPanel = (settings = []) => ({
  label: TABLE_SETTINGS_PANEL,
  icon: FieldsIcon,
  settings,
})
